/**
 * Utility functions for handling contact information (phone vs URL detection)
 */

/**
 * Checks if a string is a phone number
 * Matches formats like: +1234567890, (123) 456-7890, 123-456-7890, 1234567890
 */
export const isPhoneNumber = (value: string): boolean => {
  if (!value) return false;
  // Remove all non-digit characters except + at the start
  const cleaned = value.replace(/[^\d+]/g, "");
  // Phone numbers typically have 10-15 digits (including country code)
  // Check if it starts with + or has 10+ digits
  return /^\+?\d{10,15}$/.test(cleaned);
};

/**
 * Checks if a string is a URL
 */
export const isUrl = (value: string): boolean => {
  if (!value) return false;
  // Check for common URL patterns
  return (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("www.") ||
    /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}/.test(value)
  );
};

/**
 * Returns the appropriate href for a contact value
 * - Phone numbers get tel: prefix
 * - URLs get https:// prefix if needed
 * - Email addresses get mailto: prefix
 */
export const getContactHref = (value: string): string => {
  if (!value) return "#";

  if (isPhoneNumber(value)) {
    // Clean and format as tel: link
    const cleaned = value.replace(/[^\d+]/g, "");
    return `tel:${cleaned}`;
  }

  if (value.includes("@")) {
    return `mailto:${value}`;
  }

  if (isUrl(value)) {
    // Add https:// if no protocol specified
    if (!value.startsWith("http://") && !value.startsWith("https://")) {
      return `https://${value}`;
    }
    return value;
  }

  return value;
};

/**
 * Resolves the property-page contact phone/email from the assigned team member
 * and the org config, using the precedence the quick-contact banner relies on:
 *   team member -> primary contact (only when flagged useForPropertyPage)
 *   -> org inbound/default.
 * Returns `undefined` when nothing is configured, so callers render no link
 * instead of an invalid `tel:undefined` / `mailto:undefined`.
 */
interface PropertyContactSources {
  teamMember?: { phone_number?: string | null; email?: string | null } | null;
  orgData?: {
    primary_contact?: {
      useForPropertyPage?: boolean | null;
      phone?: string | null;
      email?: string | null;
    } | null;
    phoneNumbers?: { inbound?: string | null } | null;
    email?: string | null;
  } | null;
}

export const resolvePropertyContactPhone = (
  sources: PropertyContactSources
): string | undefined => {
  const pc = sources.orgData?.primary_contact;
  return (
    sources.teamMember?.phone_number ||
    (pc?.useForPropertyPage ? pc?.phone : undefined) ||
    sources.orgData?.phoneNumbers?.inbound ||
    undefined
  ) || undefined;
};

export const resolvePropertyContactEmail = (
  sources: PropertyContactSources
): string | undefined => {
  const pc = sources.orgData?.primary_contact;
  return (
    sources.teamMember?.email ||
    (pc?.useForPropertyPage ? pc?.email : undefined) ||
    sources.orgData?.email ||
    undefined
  ) || undefined;
};

/**
 * Determines if a contact link should open in a new tab
 * - URLs should open in new tab
 * - Phone/email links should not
 */
export const shouldOpenInNewTab = (value: string): boolean => {
  if (!value) return false;
  return isUrl(value) && !value.includes("@");
};

/**
 * Gets display text for a contact value
 * - Phone numbers get formatted
 * - URLs show the domain or full URL
 */
export const getContactDisplayText = (
  value: string,
  defaultText?: string
): string => {
  if (!value) return defaultText || "";

  if (isPhoneNumber(value)) {
    // Return as-is, let formatPhone handle it
    return value;
  }

  return defaultText || value;
};
